import React, { useState } from "react";
import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Alert,
  Collapse,
  Button,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import Div from "@jumbo/shared/Div";
import { ASSET_CODINGPAL } from "../../../utils/constants/paths";
import * as yup from "yup";
import { Form, Formik } from "formik";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import authServices from "../../../services/auth-services";
import { useNavigate, useLocation } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { ROUTES } from "app/utils/constants/routeConstants";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import useMetaTitle from "app/hooks/useMetaTitle";
import { useTheme } from "@mui/material/styles";

const validationSchema = yup.object({
  email: yup
    .string("Enter your Email/Username")
    // .email("Enter a valid email")
    .required("Email/Username is required"),
  password: yup.string("Enter your password").required("Password is required"),
});

const Login2 = ({ disableSmLogin }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { setAuthToken } = useJumboAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const getErrorMessage = (err) => {
    if (err.response?.status === 401) {
      return "Invalid email/username or password. Please try again.";
    }
    return "An error occurred during sign-in. Please try again later.";
  };

  const onSignIn = async (username, password, setSubmitting) => {
    try {
      const data = await authServices.signIn({
        username,
        password,
      });
      setAuthToken(data?.access_token);

      // Check for redirectURI in query parameters
      const queryParams = new URLSearchParams(location.search);
      const redirectURI = queryParams.get("redirectURI");

      // Navigate to redirectURI if present, otherwise to home
      navigate(
        redirectURI ? decodeURIComponent(redirectURI) : ROUTES.USER_HOME
      );
    } catch (err) {
      setError(getErrorMessage(err));
      setOpen(true);
      setSubmitting(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  useMetaTitle({
    title: "Sign In",
  });

  return (
    <Div
      sx={{
        width: 720,
        maxWidth: "100%",
        margin: "auto",
        p: 4,
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("md")]: {
          height: "calc(100vh - 130px)",
        },
      }}
    >
      <Card
        sx={{
          display: "flex",
          minWidth: 0,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <CardContent
          sx={{
            flex: "0 1 300px",
            position: "relative",
            backgroundColor: "#222d45",
            backgroundSize: "cover",
            "&::after": {
              display: "inline-block",
              position: "absolute",
              content: `''`,
              inset: 0,
            },
          }}
        >
          <Div
            sx={{
              display: "flex",
              minWidth: 0,
              flex: 1,
              flexDirection: "column",
              color: "common.white",
              position: "relative",
              zIndex: 1,
              height: "100%",
            }}
          >
            <Div sx={{ mb: 2 }}>
              <Div
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  mt: "auto",
                  mb: 3,
                }}
              >
                <img
                  src={`${ASSET_CODINGPAL}/codingpal.jpg`}
                  alt="CodingPal"
                  width={60}
                  height={60}
                />
                <Typography
                  variant="h1"
                  color="white"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: 0,
                  }}
                >
                  CodingPal
                </Typography>
              </Div>
              <Typography
                variant={"h3"}
                color={"inherit"}
                fontWeight={500}
                mb={3}
              >
                Sign In
              </Typography>
              <Typography variant={"body1"} mb={2}>
                By signing in, you can avail full features of CodingPal such as
                DSA Problems on various topics, Code editor, Code Judge and Many
                more...
              </Typography>
            </Div>
          </Div>
        </CardContent>
        <CardContent sx={{ flex: 1, p: 4 }}>
          <Collapse in={open}>
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpen(false);
                    setError(null);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          </Collapse>
          <Formik
            validateOnChange={true}
            initialValues={{
              email: "",
              password: "",
            }}
            validationSchema={validationSchema}
            onSubmit={(data, { setSubmitting }) => {
              setSubmitting(true);
              onSignIn(data.email, data.password, setSubmitting);
            }}
          >
            {({ isSubmitting }) => (
              <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                <Div sx={{ mt: 1, mb: 3 }}>
                  <JumboTextField
                    fullWidth
                    name="email"
                    label="Email/Username"
                  />
                </Div>
                <Div sx={{ mt: 1, mb: 2 }}>
                  <JumboTextField
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Div>
                <Div sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Remember me"
                  />
                </Div>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    mb: 2,
                    backgroundColor: "#222d45",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#222d45",
                    },
                  }}
                  loading={isSubmitting}
                >
                  Sign In
                </LoadingButton>
                <Div
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    sx={{
                      color: "#475259",
                      textTransform: "none",
                    }}
                    onClick={() => {
                      navigate(ROUTES.USER_SIGNUP);
                    }}
                  >
                    Sign Up
                  </Button>
                  <Button
                    sx={{
                      color: "#475259",
                      textTransform: "none",
                    }}
                    onClick={() => {
                      navigate(ROUTES.USER_FORGOT_PASSWORD);
                    }}
                  >
                    Forgot Password?
                  </Button>
                </Div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Div>
  );
};

export default Login2;
